import { Modal, useModals } from 'almanac-ui/Modal'
import { Button } from 'blaze-ui/Button'
import { BRAND_PLAN_SOCIAL_MEDIA_CHANNELS, BrandPlanSocialMediaChannels, BrandPlanTactic } from 'types'
import Styles from './SelectAccountsModal.module.scss'
import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { Text } from 'blaze-ui/Text/Text'
import { Heading } from 'blaze-ui/Heading/Heading'
import { capitalize } from 'lodash'
import { InstagramBrand } from 'blaze-ui/icons/24/InstagramBrand'
import { FacebookBrand } from 'blaze-ui/icons/35/FacebookBrand'
import TwitterBrand from 'blaze-ui/icons/20/TwitterBrand'
import LinkedInBrand from 'blaze-ui/icons/32/LinkedInBrand'
import { RadioBullet } from 'components/RadioGroup/RadioBullet'
import { RadioGroup } from 'components/RadioGroup/RadioGroup'
import { useState } from 'react'
import { Radio } from 'components/RadioGroup'
import { AvatarWithPlatformIcon } from 'screens/Analytics/components/ExternalIntegrationSelect/AvatarWithPlatformIcon'
import { useAddExternalIntegrationsToBrandKit } from 'hooks/queries/useAddExternalIntegrationsToBrandKit'

export const TACTIC_TO_PROVIDER = {
  [BrandPlanTactic.Instagram]: ExternalIntegrationEnum.Instagram,
  [BrandPlanTactic.Facebook]: ExternalIntegrationEnum.Facebook,
  [BrandPlanTactic.LinkedIn]: ExternalIntegrationEnum.LinkedIn,
  [BrandPlanTactic.Twitter]: ExternalIntegrationEnum.X,
  [BrandPlanTactic.YouTube]: ExternalIntegrationEnum.YouTube,
  [BrandPlanTactic.Email]: ExternalIntegrationEnum.Mailchimp,
  [BrandPlanTactic.SEO]: ExternalIntegrationEnum.WordPress,
  [BrandPlanTactic.TikTok]: ExternalIntegrationEnum.TikTok,
  [BrandPlanTactic.InstagramReel]: ExternalIntegrationEnum.Instagram,
  [BrandPlanTactic.MetaAds]: ExternalIntegrationEnum.MetaAds,
  [BrandPlanTactic.GoogleBusinessProfile]: ExternalIntegrationEnum.GoogleBusinessProfile,
  google_ads: undefined,
}

export interface SelectAccountsModalProps {
  onConfirm: () => void
  isLoading: boolean
  externalIntegrations?: ExternalIntegration[]
  tactics: BrandPlanTactic[]
  brandKitId: number
}

export const SelectAccountsModal = ({
  onConfirm,
  isLoading,
  externalIntegrations,
  tactics,
  brandKitId,
}: SelectAccountsModalProps) => {
  const { closeModal } = useModals()
  const [selectedExternalIntegrations, setSelectedExternalIntegrations] = useState<ExternalIntegration[]>(
    externalIntegrations?.filter((integration) =>
      tactics.some((tactic) => integration.provider === TACTIC_TO_PROVIDER[tactic]),
    ) || [],
  )

  const { mutate: addExternalIntegrationsToBrandKit, isLoading: isAddingExternalIntegrations } =
    useAddExternalIntegrationsToBrandKit({
      onSuccess: () => {
        onConfirm()
        closeModal()
      },
    })

  const socialTactics = tactics.filter(
    (tactic) =>
      BRAND_PLAN_SOCIAL_MEDIA_CHANNELS.includes(tactic as BrandPlanSocialMediaChannels) &&
      externalIntegrations?.some((integration) => integration.provider === TACTIC_TO_PROVIDER[tactic]),
  )

  const getIcon = (tactic: BrandPlanTactic) => {
    switch (tactic) {
      case BrandPlanTactic.Instagram:
        return <InstagramBrand size={20} />
      case BrandPlanTactic.Facebook:
        return <FacebookBrand size={20} />
      case BrandPlanTactic.LinkedIn:
        return <LinkedInBrand size={20} />
      case BrandPlanTactic.Twitter:
        return <TwitterBrand size={20} />
    }
  }

  const getTacticName = (tactic: BrandPlanTactic) => {
    switch (tactic) {
      case BrandPlanTactic.Twitter:
        return 'X/Twitter'
      default:
        return capitalize(tactic)
    }
  }

  const handleConfirm = () => {
    addExternalIntegrationsToBrandKit({
      id: brandKitId,
      externalIntegrationIds: selectedExternalIntegrations.map((integration) => integration.id),
    })
  }

  return (
    <Modal.Root onPressOutside={closeModal} blaze size="sm">
      <Modal.Header title="Choose one account per channel" />
      <Modal.Content>
        <Text>For now, we can only post to one of your accounts. Please choose one to begin with.</Text>
        <div className={Styles.accountsContainer}>
          {socialTactics?.map((tactic) => (
            <div key={tactic} className={Styles.tacticContainer}>
              <div className={Styles.tacticHeader}>
                {getIcon(tactic)}
                <Heading level={5}>{getTacticName(tactic)}</Heading>
              </div>

              <RadioGroup
                aria-label="Title"
                value={selectedExternalIntegrations
                  .find((integration) => integration.provider === TACTIC_TO_PROVIDER[tactic])
                  ?.id.toString()}
                className={Styles.tacticAccounts}
                onChange={(externalIntegrationId) => {
                  setSelectedExternalIntegrations((prev) => {
                    const newExternalIntegrations = [
                      ...prev.filter((integration) => integration.provider !== TACTIC_TO_PROVIDER[tactic]),
                      externalIntegrations?.find((integration) => integration.id === Number(externalIntegrationId)),
                    ].filter((integration) => integration !== undefined)

                    return newExternalIntegrations as ExternalIntegration[]
                  })
                }}
              >
                {externalIntegrations
                  ?.filter((integration) => integration.provider === TACTIC_TO_PROVIDER[tactic])
                  .map((integration) => (
                    <Radio
                      className={Styles.titleOption}
                      value={integration.id.toString()}
                      aria-label={integration.name}
                      key={integration.id}
                      hideRadio
                    >
                      {(radioBulletProps) => (
                        <div className={Styles.accountItem}>
                          <div>
                            <RadioBullet {...radioBulletProps} size={20} />
                          </div>

                          <AvatarWithPlatformIcon
                            avatar={integration.picture}
                            platform={integration.provider}
                            hideIcon
                            size="sm"
                          />
                          <Text color="var(--dark-80)" variant="secondary">
                            {integration.name}
                          </Text>
                        </div>
                      )}
                    </Radio>
                  ))}
              </RadioGroup>
            </div>
          ))}
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Button size="lg" variant="tertiary" onPress={closeModal}>
            Cancel
          </Button>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <div className={Styles.rightButtons}>
            <Button size="lg" onPress={handleConfirm} isDisabled={isLoading || isAddingExternalIntegrations}>
              Confirm
            </Button>
          </div>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
